import { APIClient } from 'aurelio/src/abstracts/apiClient';

// To avoid no-magic-numbers.
const DEFAULT_PAGINATION_COUNT = 20;

/**
 * The app API client. It takes care of making all the requests.
 * @extends {APIClient}
 */
class AppAPI extends APIClient {
  /**
   * @param {HttpClient}       httpClient       The Aurelia's HTTP client, to make the requests.
   * @param {AppConfiguration} appConfiguration To get the API configuation.
   */
  constructor(httpClient, appConfiguration) {
    'inject';

    super(httpClient, appConfiguration.get('api'));
  }
  cancelProductFeedJob(accountId, jobId) {
    return this.delete(this.endpoint('productFeeds.jobs.cancel', { accountId, jobId }));
  }
  createProductFeedsJob(
    accountId,
    variant,
    mediaNumber,
  ) {
    return this.post(
      this.endpoint('productFeeds.jobs.new', { accountId }),
      {
        variant,
        mediaNumber,
      },
    );
  }
  getAccountActivities(accountId, page, count = DEFAULT_PAGINATION_COUNT) {
    return this.get(this.endpoint('customerAccounts.activities', {
      accountId,
      page,
      count,
    }));
  }
  getAccountProductFeedSettings(accountId) {
    return this.get(this.endpoint('productFeeds.settings', { accountId }));
  }
  /**
   * Gets a customer account _"extract"_. The extract is just the customer name and ID.
   * This is usually used on lists where you don't need the entire entity.
   * @param {Number} accountId The customer account ID.
   * @return {Promise<Object,Error>}
   */
  getCustomerAccountExtract(accountId) {
    return this.get(this.endpoint('customerAccounts.extract', { accountId }));
  }
  /**
   * Gets a list of customer accounts.
   * @param {Number} page                                The pagination page number.
   * @param {String} [search='']                         An option search query to filter the
   *                                                      accounts.
   * @param {Number} [count=DEFAULT_PAGINATION_COUNT]    How many accounts should be returned.
   * @return {Promise<Object,Error>}
   */
  getCustomerAccountsList(page, search = '', count = DEFAULT_PAGINATION_COUNT) {
    const parameters = { page, count };
    if (search) {
      parameters.search = search;
    }

    return this.get(this.endpoint('customerAccounts.list', parameters));
  }
  getFailedProductFeedsJobs() {
    return this.get(this.endpoint('productFeeds.jobs.failed'));
  }
  getProductFeedsJob(accountId, jobId) {
    return this.get(this.endpoint('productFeeds.jobs.byId', {
      accountId,
      jobId,
    }));
  }
  getProductFeedsJobEntries(accountId, jobId, fromEntry) {
    return this.get(this.endpoint('productFeeds.jobs.entries', {
      accountId,
      jobId,
      fromEntry,
    }));
  }
  getProductFeedsJobLog(accountId, jobId) {
    return this.get(
      this.endpoint('productFeeds.jobs.log', {
        accountId,
        jobId,
      }),
      { json: false },
    )
    .then((response) => response.text());
  }
  getProductFeedsJobs(
    accountId,
    page,
    count = DEFAULT_PAGINATION_COUNT,
    sortBy = null,
    sortMode = null,
  ) {
    const parameters = {
      accountId,
      page,
      count,
    };

    if (sortBy && sortMode !== null) {
      parameters.sort = sortMode === 'desc' ? `-${sortBy}` : sortBy;
    }

    return this.get(this.endpoint('productFeeds.jobs.list', parameters));
  }
  getProductFeedsJobVariants(accountId) {
    return this.get(this.endpoint('productFeeds.jobs.variants', { accountId }));
  }
  getProductFeedsProductCategories(accountId, productId) {
    return this.get(this.endpoint('productFeeds.products.categories', {
      accountId,
      productId,
    }));
  }
  getProductFeedsProductExtract(accountId, productId) {
    return this.get(this.endpoint('productFeeds.products.extract', {
      accountId,
      productId,
    }));
  }
  getProductFeedsProductExtras(accountId, productId) {
    return this.get(this.endpoint('productFeeds.products.extras', {
      accountId,
      productId,
    }));
  }
  getProductFeedsProductIds(accountId, productId) {
    return this.get(this.endpoint('productFeeds.products.ids', {
      accountId,
      productId,
    }));
  }
  getProductFeedsProducts(
    accountId,
    page,
    search = '',
    count = DEFAULT_PAGINATION_COUNT,
    sortBy = null,
    sortMode = null,
  ) {
    const parameters = {
      accountId,
      page,
      count,
    };
    if (search) {
      parameters.search = search;
    }

    if (sortBy && sortMode !== null) {
      parameters.sort = sortMode === 'desc' ? `-${sortBy}` : sortBy;
    }

    return this.get(this.endpoint('productFeeds.products.list', parameters));
  }
  getProductFeedsProductSettings(accountId, productId) {
    return this.get(this.endpoint('productFeeds.products.settings', {
      accountId,
      productId,
    }));
  }
  getProductFeedsProductSyndication(accountId, productId) {
    return this.get(this.endpoint('productFeeds.products.syndication', {
      accountId,
      productId,
    }));
  }
  saveAccountProductFeedSettings(accountId, settings) {
    return this.post(
      this.endpoint('productFeeds.settings', { accountId }),
      { settings },
    );
  }
  updateProductFeedJob(accountId, jobId, properties) {
    return this.post(
      this.endpoint('productFeeds.jobs.update', { accountId, jobId }),
      properties,
    );
  }
}

export { AppAPI };
